/**
 * F&I product benchmarks — the penetration and average-income bars the F&I
 * Scoreboard's product cards are measured against.
 *
 * These are rates, not accumulating totals, so unlike the monthly unit and
 * gross targets they are NOT prorated to days elapsed: a 60% ESP penetration is
 * the bar on day 3 just as much as on day 30. That's why they're a flat
 * per-dealership setting rather than twelve monthly rows.
 *
 * Configured under Targets → Sales (the `fiProducts` key on the front-end
 * targets blob) and applied at report time, so changing a benchmark restates
 * how every past month reads — the deals don't move, the bar does.
 *
 * The product list itself is fixed. Each entry maps to a column on the deal
 * record, so a dealership can retune the bars but can't add a product the deal
 * form has nowhere to record. Labels are the showroom names for those columns
 * (the deal entry form calls ESP "VSC" and Appearance "Paint").
 *
 * The defaults below are the industry-standard starting point, the same role
 * DEFAULT_BENCHMARKS plays for Fixed Ops. There is no backend mirror because
 * the F&I Scoreboard computes these client-side; if a server-side report ever
 * needs them, it needs a counterpart of this file the way docFee has one.
 */

export const FI_PRODUCT_DEFAULTS = [
  { field: 'vscAmount', label: 'ESP', penetrationTarget: 60, averageTarget: 1000 },
  { field: 'ppmAmount', label: 'PPM', penetrationTarget: 40, averageTarget: 1000 },
  { field: 'tireAmount', label: 'Tire', penetrationTarget: 40, averageTarget: 350 },
  { field: 'gapAmount', label: 'GAP', penetrationTarget: 40, averageTarget: 500 },
  { field: 'theftAmount', label: 'Theft', penetrationTarget: 15, averageTarget: 500 },
  { field: 'paintAmount', label: 'Appearance', penetrationTarget: 15, averageTarget: 500 }
];

/**
 * Read one configured bar, falling back to the default when it's missing or
 * non-positive.
 *
 * A zero bar looks like a deliberate "no goal" but doesn't behave like one: it
 * would peg the card at 0% attainment forever and paint the product permanently
 * red. Treating it as unset matches how the Fixed Ops benchmarks resolve, so a
 * half-filled or cleared field can't quietly break a card.
 */
function readBar(raw, key, fallback) {
  const value = Number(raw && raw[key]);
  return Number.isFinite(value) && value > 0 ? value : fallback;
}

/**
 * The product list with each dealership's configured bars applied. Takes a
 * front-end targets object (the same one getDocFee reads) and always returns
 * all six products in their canonical order, so the board's card layout doesn't
 * depend on what happens to be saved.
 */
export function resolveFiProducts(targets) {
  const saved = (targets && targets.fiProducts) || {};

  return FI_PRODUCT_DEFAULTS.map((product) => {
    const raw = saved[product.field];
    return {
      ...product,
      penetrationTarget: readBar(raw, 'penetrationTarget', product.penetrationTarget),
      averageTarget: readBar(raw, 'averageTarget', product.averageTarget)
    };
  });
}

/**
 * The same resolution, keyed by field, for the Targets editor to hold in state
 * and save back. Editing starts from the resolved values rather than from
 * blanks, so a dealership that has never touched this sees the defaults it has
 * actually been measured against instead of an empty form.
 */
export function normalizeFiProducts(raw) {
  const normalized = {};
  resolveFiProducts({ fiProducts: raw }).forEach((product) => {
    normalized[product.field] = {
      penetrationTarget: product.penetrationTarget,
      averageTarget: product.averageTarget
    };
  });
  return normalized;
}
